import React, { useEffect, useRef } from 'react';
import { Calendar, ArrowRight, Clock, DollarSign, BarChart4, PercentCircle, Target, AlertTriangle } from 'lucide-react';
import { Result } from '../types';
import InsightCard from './InsightCard';

interface ResultsSectionProps {
  results: Result;
  onSchedule: () => void;
}

const ResultsSection: React.FC<ResultsSectionProps> = ({ results, onSchedule }) => {
  const scoreRingRef = useRef<SVGCircleElement>(null);

  useEffect(() => {
    if (scoreRingRef.current) {
      const circumference = 2 * Math.PI * 54;
      const offset = circumference - (results.score / 100) * circumference;
      
      scoreRingRef.current.style.strokeDasharray = `${circumference}`;
      scoreRingRef.current.style.strokeDashoffset = `${circumference}`;
      
      setTimeout(() => {
        if (scoreRingRef.current) {
          scoreRingRef.current.style.transition = 'stroke-dashoffset 1.5s ease-in-out';
          scoreRingRef.current.style.strokeDashoffset = `${offset}`;
        }
      }, 300);
    }
  }, [results.score]);

  return (
    <div className="max-w-5xl mx-auto px-4 py-16 md:py-24">
      <div className="text-center mb-16">
        <h2 className="text-3xl md:text-4xl font-bold text-[#103541] mb-4">
          Seu Diagnóstico de Produtividade
        </h2>
        <p className="text-lg text-[#2E6173] max-w-2xl mx-auto mb-2">
          Análise completa do potencial de automação fiscal da sua empresa
        </p>
        <p className="text-sm text-[#2E6173] max-w-2xl mx-auto italic">
          Os dados usados para o cálculo são baseados em um grupo de estudo de clientes da NFE
        </p>
      </div>
      
      {/* Score Card */}
      <div className="bg-white rounded-2xl p-8 md:p-10 shadow-lg mb-16 grid md:grid-cols-2 gap-8 md:gap-12">
        <div className="flex items-center gap-8">
          <div className="relative w-36 h-36 flex-shrink-0">
            <svg viewBox="0 0 120 120" width="100%" height="100%">
              <circle cx="60" cy="60" r="54" fill="none" stroke="#e5e7eb" strokeWidth="12" />
              <circle 
                ref={scoreRingRef}
                cx="60" 
                cy="60" 
                r="54" 
                fill="none" 
                stroke="#7AEA53" 
                strokeWidth="12"
                strokeDasharray="339.292" 
                strokeDashoffset="339.292"
                transform="rotate(-90 60 60)"
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <div className="text-5xl font-bold text-[#103541]">{results.score}</div>
              <div className="text-sm text-[#2E6173]">pontos</div>
            </div>
          </div>
          
          <div>
            <div className={`inline-block px-4 py-2 rounded-full font-medium mb-3 ${
              results.categoryClass === 'category-low' ? 'bg-green-100 text-green-800' :
              results.categoryClass === 'category-medium' ? 'bg-yellow-100 text-yellow-800' :
              results.categoryClass === 'category-high' ? 'bg-orange-100 text-orange-800' :
              'bg-red-100 text-red-800'
            }`}>
              {results.category}
            </div>
            <p className="text-[#2E6173] text-lg">{results.categoryDescription}</p>
          </div>
        </div>
        
        <div className="border-t md:border-t-0 md:border-l border-gray-100 pt-8 md:pt-0 md:pl-12">
          <h3 className="text-2xl font-semibold text-[#103541] mb-6">Situação Atual</h3>
          <div className="space-y-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-[#f9fff5] flex items-center justify-center text-[#7AEA53]">
                <Clock size={24} />
              </div>
              <div>
                <div className="text-xl font-semibold text-[#103541]">{results.currentState.hoursPerDay}</div>
                <div className="text-[#2E6173]">em tarefas manuais</div>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-[#f9fff5] flex items-center justify-center text-[#7AEA53]">
                <BarChart4 size={24} />
              </div>
              <div>
                <div className="text-xl font-semibold text-[#103541]">{results.currentState.errorFrequency}</div>
                <div className="text-[#2E6173]">corrigindo erros</div>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-[#f9fff5] flex items-center justify-center text-[#7AEA53]">
                <DollarSign size={24} />
              </div>
              <div>
                <div className="text-xl font-semibold text-[#103541]">{results.currentState.monthlyCost}</div>
                <div className="text-[#2E6173]">custo mensal estimado</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Comparison Section */}
      <div className="mb-12">
        <h3 className="text-xl font-semibold text-[#103541] mb-8">Comparativo: Antes vs Depois da Automação</h3>
        <div className="grid grid-cols-1 md:grid-cols-7 gap-6">
          <div className="md:col-span-3 bg-red-50 rounded-2xl p-6 border border-red-100">
            <h4 className="text-lg font-semibold text-center mb-6 text-[#103541]">Hoje (Manual)</h4>
            <div className="space-y-5">
              <div className="flex items-start gap-3">
                <Clock className="text-red-500 flex-shrink-0 mt-0.5" size={20} />
                <div>
                  <span className="font-semibold text-[#103541]">{results.comparison.manual.hours}h</span>
                  <span className="text-[#2E6173] text-sm"> por semana em tarefas manuais</span>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <BarChart4 className="text-red-500 flex-shrink-0 mt-0.5" size={20} />
                <div>
                  <span className="font-semibold text-[#103541]">{results.comparison.manual.documents}+</span>
                  <span className="text-[#2E6173] text-sm"> documentos processados manualmente</span>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <AlertTriangle className="text-red-500 flex-shrink-0 mt-0.5" size={20} />
                <div>
                  <span className="font-semibold text-[#103541]">{results.comparison.manual.errorRate}%</span>
                  <span className="text-[#2E6173] text-sm"> taxa de erro média</span>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <AlertTriangle className="text-red-500 flex-shrink-0 mt-0.5" size={20} />
                <div>
                  <span className="font-semibold text-[#103541]">Alto stress</span>
                  <span className="text-[#2E6173] text-sm"> com prazos fiscais</span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="hidden md:flex items-center justify-center">
            <div className="w-12 h-12 rounded-full bg-[#7AEA53] text-white flex items-center justify-center transform rotate-0 md:rotate-0">
              <ArrowRight size={24} />
            </div>
          </div>
          
          <div className="md:col-span-3 bg-green-50 rounded-2xl p-6 border border-green-100">
            <h4 className="text-lg font-semibold text-center mb-6 text-[#103541]">Com Automação</h4>
            <div className="space-y-5">
              <div className="flex items-start gap-3">
                <Clock className="text-[#7AEA53] flex-shrink-0 mt-0.5" size={20} />
                <div>
                  <span className="font-semibold text-[#103541]">{results.comparison.automated.hours}h</span>
                  <span className="text-[#2E6173] text-sm"> por semana (80% redução)</span>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <BarChart4 className="text-[#7AEA53] flex-shrink-0 mt-0.5" size={20} />
                <div>
                  <span className="font-semibold text-[#103541]">{results.comparison.automated.documents}</span>
                  <span className="text-[#2E6173] text-sm"> documentos sem esforço adicional</span>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <AlertTriangle className="text-[#7AEA53] flex-shrink-0 mt-0.5" size={20} />
                <div>
                  <span className="font-semibold text-[#103541]">{results.comparison.automated.errorRate}%</span>
                  <span className="text-[#2E6173] text-sm"> taxa de erro (95% redução)</span>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Target className="text-[#7AEA53] flex-shrink-0 mt-0.5" size={20} />
                <div>
                  <span className="font-semibold text-[#103541]">Tranquilidade</span>
                  <span className="text-[#2E6173] text-sm"> com compliance automático</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Savings Section */}
      <div className="mb-12">
        <h3 className="text-xl font-semibold text-[#103541] mb-8">Economia Projetada</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white rounded-xl p-6 border border-gray-100 text-center transition-all hover:-translate-y-1 hover:shadow-md duration-300">
            <div className="text-[#7AEA53] mb-4">
              <Clock className="mx-auto" size={36} />
            </div>
            <div className="text-2xl font-bold text-[#7AEA53] mb-2">{results.savings.weeklyHours}h</div>
            <div className="text-sm font-semibold text-[#103541] mb-1">Horas Economizadas/Semana</div>
            <div className="text-xs text-[#2E6173]">Tempo para atividades estratégicas</div>
          </div>
          
          <div className="bg-white rounded-xl p-6 border border-gray-100 text-center transition-all hover:-translate-y-1 hover:shadow-md duration-300">
            <div className="text-[#7AEA53] mb-4">
              <DollarSign className="mx-auto" size={36} />
            </div>
            <div className="text-2xl font-bold text-[#7AEA53] mb-2">{results.savings.monthlyAmount}</div>
            <div className="text-sm font-semibold text-[#103541] mb-1">Economia Mensal</div>
            <div className="text-xs text-[#2E6173]">Redução de custos operacionais</div>
          </div>
          
          <div className="bg-white rounded-xl p-6 border border-gray-100 text-center transition-all hover:-translate-y-1 hover:shadow-md duration-300">
            <div className="text-[#7AEA53] mb-4">
              <BarChart4 className="mx-auto" size={36} />
            </div>
            <div className="text-2xl font-bold text-[#7AEA53] mb-2">{results.savings.annualAmount}</div>
            <div className="text-sm font-semibold text-[#103541] mb-1">Economia Anual</div>
            <div className="text-xs text-[#2E6173]">Valor acumulado em 12 meses</div>
          </div>
          
          <div className="bg-white rounded-xl p-6 border border-gray-100 text-center transition-all hover:-translate-y-1 hover:shadow-md duration-300">
            <div className="text-[#7AEA53] mb-4">
              <Target className="mx-auto" size={36} />
            </div>
            <div className="text-2xl font-bold text-[#7AEA53] mb-2">{results.savings.roiPeriod}</div>
            <div className="text-sm font-semibold text-[#103541] mb-1">Retorno do Investimento</div>
            <div className="text-xs text-[#2E6173]">Payback completo da solução</div>
          </div>
        </div>
      </div>
      
      {/* Executive Summary */}
      <div className="mb-12">
        <h3 className="text-xl font-semibold text-[#103541] mb-8">Resumo Executivo</h3>
        <div className="bg-white rounded-xl border border-[#5DD0F2] overflow-hidden">
          <div className="bg-[#5DD0F2] p-5 text-white flex items-center gap-3">
            <BarChart4 size={24} />
            <h4 className="font-semibold text-xl">Relatório para Apresentação</h4>
          </div>
          <div className="p-6 space-y-4">
            <p className="text-[#103541]">
              <strong>Situação Atual:</strong> A empresa dedica <span className="text-[#7AEA53] font-medium">{results.currentState.hoursPerWeek} horas semanais</span> em processos manuais fiscais, com uma taxa de erro de <span className="text-[#7AEA53] font-medium">{results.comparison.manual.errorRate}%</span> e custo operacional mensal de <span className="text-[#7AEA53] font-medium">{results.currentState.monthlyCost}</span>.
            </p>
            <p className="text-[#103541]">
              <strong>Oportunidade:</strong> A automação fiscal pode reduzir <span className="text-[#7AEA53] font-medium">80%</span> do tempo gasto, economizar <span className="text-[#7AEA53] font-medium">{results.savings.monthlyAmount}/mês</span> e reduzir erros em <span className="text-[#7AEA53] font-medium">95%</span>.
            </p>
            <p className="text-[#103541]">
              <strong>ROI:</strong> Investimento se paga em <span className="text-[#7AEA53] font-medium">{results.savings.roiPeriod}</span> com economia anual projetada de <span className="text-[#7AEA53] font-medium">{results.savings.annualAmount}</span>.
            </p>
          </div>
        </div>
      </div>
      
      {/* Insights Section */}
      {results.insights.length > 0 && (
        <div className="mb-12">
          <h3 className="text-xl font-semibold text-[#103541] mb-8">Insights Personalizados</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {results.insights.map((insight, index) => (
              <InsightCard key={index} insight={insight} />
            ))}
          </div>
        </div>
      )}
      
      {/* CTA Section */}
      <div className="bg-[#103541] rounded-2xl p-8 md:p-12 text-center">
        <h3 className="text-2xl font-bold text-white mb-4">Transforme esses números em realidade</h3>
        <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
          Agende uma demonstração personalizada e descubra como podemos revolucionar 
          a operação fiscal da sua empresa
        </p>
        <button 
          onClick={onSchedule}
          className="py-4 px-8 bg-[#7AEA53] text-[#103541] font-semibold rounded-xl transition-all hover:bg-[#8FFA69] hover:shadow-lg hover:-translate-y-1 flex items-center justify-center gap-2 mx-auto"
        >
          <Calendar size={20} />
          Agendar Demonstração
        </button>
      </div>
    </div>
  );
};

export default ResultsSection;